import { useOutletContext, Link } from 'react-router';
import { TrendingUp, TrendingDown, XCircle, ArrowRight } from 'lucide-react';
import { Button, Card, NotificationCard } from '@sighthost/ui';
import type { Service } from '@/domains/hosting';

/**
 * Service management actions (upgrade / downgrade / cancel).
 *
 * Self-service plan-change UI is deferred (admin-side AD-17 saga shipped;
 * customer-side wizard is Sprint 3 territory). Until then, route customers
 * to support — SightHost ops will quote and execute the plan-change saga
 * on their behalf.
 */
export default function ServiceManageTab() {
  const service = useOutletContext<Service>();
  const isActive = service.status === 'active';

  const upgradeSubject = `Plan change request — ${service.primaryDomain ?? service.cpanelUsername} (upgrade)`;
  const downgradeSubject = `Plan change request — ${service.primaryDomain ?? service.cpanelUsername} (downgrade)`;
  const cancelSubject = `Cancel service — ${service.primaryDomain ?? service.cpanelUsername}`;

  return (
    <div className="space-y-6">
      <ActionCard
        icon={TrendingUp}
        title="Upgrade plan"
        description="Move to a larger plan with more disk, bandwidth, and email accounts. Open a ticket — we'll prepare a quote within 1 business day and prorate it against your current cycle."
        button={
          <Button asChild variant="outline" size="sm">
            <Link to={`/app/support/new?dept=billing&subject=${encodeURIComponent(upgradeSubject)}`}>
              Open a ticket
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        }
      />

      <ActionCard
        icon={TrendingDown}
        title="Downgrade plan"
        description="Move to a smaller plan, effective at the start of your next billing cycle. Open a ticket — we'll confirm your usage fits the new limits before the change is applied."
        button={
          <Button asChild variant="outline" size="sm">
            <Link to={`/app/support/new?dept=billing&subject=${encodeURIComponent(downgradeSubject)}`}>
              Open a ticket
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        }
      />

      <ActionCard
        icon={XCircle}
        title="Cancel service"
        description="Request immediate cancellation, or keep the service running until the end of the current billing period. We retain backups for 30 days after termination."
        button={
          <Button asChild variant="outline" size="sm">
            <Link to={`/app/support/new?dept=billing&subject=${encodeURIComponent(cancelSubject)}`}>
              Open a ticket
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        }
        tone="danger"
      />

      {!isActive && (
        <NotificationCard
          tone="warning"
          title="Some actions are unavailable"
          body={
            <>
              This service is currently <strong className="font-medium">{service.status}</strong>.
              Some management actions are unavailable until status returns to <em>active</em>.
            </>
          }
        />
      )}
    </div>
  );
}

function ActionCard({
  icon: Icon,
  title,
  description,
  button,
  tone,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  button: React.ReactNode;
  tone?: 'danger';
}) {
  return (
    <Card
      interactive={false}
      className={`flex items-start gap-4 p-6 ${tone === 'danger' ? 'border-danger/30' : ''}`}
    >
      <div
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded ${
          tone === 'danger' ? 'bg-danger/10 text-danger' : 'bg-muted text-foreground-soft'
        }`}
      >
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1">
        <h3 className="mb-1 text-sm font-semibold text-ink">{title}</h3>
        <p className="text-sm text-foreground-soft">{description}</p>
      </div>
      <div className="shrink-0">{button}</div>
    </Card>
  );
}
